<script setup lang="ts">
import { ref, computed } from 'vue';
import {
  UiButton, UiIcon, UiText, UiAside, UiDivider,
  UiToggleGroup, UiToggleButton,
} from '@shopexperts/shared';
import {
  claimDecisionFilterOptions,
  claimStatusFilterOptions,
  claimTypeFilterOptions,
} from './claimAppearance';

const decision = defineModel<string>('decision', { default: '' });
const status = defineModel<string>('status', { default: '' });
const type = defineModel<string>('type', { default: '' });

const emit = defineEmits<{
  reset: [];
}>();

const isOpen = ref(false);

const hasActiveFilters = computed(() => {
  return !!(decision.value || status.value || type.value);
});

const activeFilterCount = computed(() => {
  return [decision.value, status.value, type.value].filter(Boolean).length;
});
</script>

<template>
  <!-- Filter button only — active-filter tags are rendered by the parent,
       which owns the v-model state the tags derive from. -->
  <UiButton
    variant="outline"
    size="md"
    icon="filter"
    label="Filters"
    @click="isOpen = true"
  >
    <template v-if="activeFilterCount > 0" #after_label>
      <span class="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-se-primary text-xs font-semibold text-se-dark">
        {{ activeFilterCount }}
      </span>
    </template>
  </UiButton>

  <!-- Filter Aside Panel -->
  <UiAside v-model="isOpen" position="right">
    <div class="flex flex-col h-full">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-100">
        <div>
          <UiText as="h2" variant="h3" text="Filters" class="text-gray-900" />
          <UiText
            v-if="hasActiveFilters"
            as="p"
            variant="caption-normal"
            class="text-gray-400 mt-0.5"
            :text="`${activeFilterCount} active ${activeFilterCount === 1 ? 'filter' : 'filters'}`"
          />
        </div>
        <button
          class="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
          aria-label="Close filters"
          @click="isOpen = false"
        >
          <UiIcon name="close" class="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <!-- Filter sections -->
      <div class="flex-1 overflow-y-auto p-6 space-y-7">

        <!-- Decision -->
        <div>
          <UiText as="label" variant="caption" class="text-gray-500 tracking-wide block mb-3" text="Decision" />
          <UiToggleGroup v-model="decision">
            <div class="flex flex-wrap gap-2">
              <UiToggleButton value="" label="All" variant="outline" />
              <UiToggleButton
                v-for="option in claimDecisionFilterOptions"
                :key="option.value"
                :value="option.value"
                variant="outline"
              >
                <template #before_label>
                  <span class="w-2 h-2 rounded-full shrink-0" :class="option.dot" />
                </template>
                <template #label>
                  <span class="text-gray-600">{{ option.label }}</span>
                </template>
              </UiToggleButton>
            </div>
          </UiToggleGroup>
        </div>

        <UiDivider color="light" />

        <!-- Email verification status -->
        <div>
          <UiText as="label" variant="caption" class="text-gray-500 tracking-wide block mb-3" text="Email verification" />
          <UiToggleGroup v-model="status">
            <div class="flex flex-wrap gap-2">
              <UiToggleButton value="" label="All" variant="outline" />
              <UiToggleButton
                v-for="option in claimStatusFilterOptions"
                :key="option.value"
                :value="option.value"
                variant="outline"
              >
                <template #before_label>
                  <span class="w-2 h-2 rounded-full shrink-0" :class="option.dot" />
                </template>
                <template #label>
                  <span class="text-gray-600">{{ option.label }}</span>
                </template>
              </UiToggleButton>
            </div>
          </UiToggleGroup>
        </div>

        <UiDivider color="light" />

        <!-- Claim type -->
        <div>
          <UiText as="label" variant="caption" class="text-gray-500 tracking-wide block mb-3" text="Claim type" />
          <UiToggleGroup v-model="type">
            <div class="flex gap-2">
              <UiToggleButton value="" label="All" variant="outline" />
              <UiToggleButton
                v-for="option in claimTypeFilterOptions"
                :key="option.value"
                :value="option.value"
                :label="option.label"
                variant="outline"
              />
            </div>
          </UiToggleGroup>
        </div>
      </div>

      <!-- Footer -->
      <div class="p-6 border-t border-gray-100 flex items-center justify-between gap-3">
        <UiButton
          v-if="hasActiveFilters"
          variant="outline"
          size="md"
          icon="close"
          label="Clear All"
          @click="emit('reset')"
        />
        <div v-else />
        <UiButton
          variant="primary"
          size="md"
          label="Done"
          @click="isOpen = false"
        />
      </div>
    </div>
  </UiAside>
</template>
